"use client"

import { useState } from "react"
import { Textarea, Button, Group, Text, Stack, Alert, Tooltip, ActionIcon } from "@mantine/core"
import { TbInfoCircle, TbCheck, TbAlertCircle } from "react-icons/tb"
import { parseCoordinates, type Coordinate } from "./parser"

export type { Coordinate }

export interface BulkCoordinateInputProps {
  onImport: (coordinates: Coordinate[]) => void
  onCancel?: () => void
  placeholder?: string
  label?: string
}

const FORMAT_EXAMPLES = (
  <div style={{ fontFamily: "monospace", fontSize: "12px" }}>
    <div style={{ marginBottom: "12px" }}>
      <div style={{ fontWeight: "bold", marginBottom: "6px" }}>Supported Formats:</div>
    </div>

    <div style={{ marginBottom: "12px" }}>
      <div style={{ fontWeight: "bold", marginBottom: "4px" }}>GeoJSON Polygon:</div>
      <div>{"{"}type: "Polygon", coordinates: [[[lng, lat], [lng, lat]]]{"}"}</div>
    </div>

    <div style={{ marginBottom: "12px" }}>
      <div style={{ fontWeight: "bold", marginBottom: "4px" }}>WKT Polygon:</div>
      <div>POLYGON((lng lat, lng lat, lng lat, lng lat))</div>
    </div>

    <div style={{ marginBottom: "12px" }}>
      <div style={{ fontWeight: "bold", marginBottom: "4px" }}>JSON Array:</div>
      <div>[{"{"}lat: 1.23, lng: 4.56{"}"}, {"{"}lat: 7.89, lng: 10.11{"}"}]</div>
    </div>

    <div style={{ marginBottom: "12px" }}>
      <div style={{ fontWeight: "bold", marginBottom: "4px" }}>CSV:</div>
      <div>lat,lng</div>
      <div>1.23,4.56</div>
      <div>7.89,10.11</div>
    </div>

    <div style={{ marginBottom: "12px" }}>
      <div style={{ fontWeight: "bold", marginBottom: "4px" }}>TSV/Space-separated:</div>
      <div>1.23 4.56</div>
      <div>7.89 10.11</div>
    </div>

    <div>
      <div style={{ fontWeight: "bold", marginBottom: "4px" }}>Individual JSON objects:</div>
      <div>{"{"}lat: 1.23, lng: 4.56{"}"}</div>
      <div>{"{"}lat: 7.89, lng: 10.11{"}"}</div>
    </div>
  </div>
)

export function BulkCoordinateInput({ onImport, onCancel, placeholder, label }: BulkCoordinateInputProps) {
  const [input, setInput] = useState("")
  const [parseResult, setParseResult] = useState<{ valid: Coordinate[]; invalid: any[]; warnings: string[] } | null>(null)
  const [showErrors, setShowErrors] = useState(false)

  const handleParse = () => {
    const result = parseCoordinates(input)
    setParseResult(result)
    setShowErrors(result.invalid.length > 0)
  }

  const handleImportValid = () => {
    if (parseResult && parseResult.valid.length >= 3) {
      onImport(parseResult.valid)
      setInput("")
      setParseResult(null)
      setShowErrors(false)
    }
  }

  const handleImportAll = () => {
    if (parseResult && parseResult.valid.length >= 3 && parseResult.invalid.length === 0) {
      onImport(parseResult.valid)
      setInput("")
      setParseResult(null)
    }
  }

  const handleClear = () => {
    setInput("")
    setParseResult(null)
    setShowErrors(false)
  }

  return (
    <Stack gap="sm">
      <Group justify="flex-start" gap="md" align="center">
        <Text size="sm" fw={500}>
          {label || "Paste Coordinates"}
        </Text>
        <Tooltip label={FORMAT_EXAMPLES} multiline w={450} withArrow position="left">
          <ActionIcon variant="subtle" size="sm">
            <TbInfoCircle size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Textarea
        placeholder={placeholder || "Paste coordinates here...\n\nExample:\n12.34, 56.78\n12.35, 56.79\n12.36, 56.80"}
        value={input}
        onChange={(e) => setInput(e.currentTarget.value)}
        rows={8}
        styles={{
          input: {
            fontFamily: "monospace",
            fontSize: "0.875rem"
          }
        }}
      />

      {parseResult && (
        <Alert
          icon={parseResult.invalid.length === 0 ? <TbCheck size={16} /> : <TbAlertCircle size={16} />}
          color={parseResult.invalid.length === 0 ? "green" : "yellow"}
          title={`${parseResult.valid.length} valid, ${parseResult.invalid.length} invalid`}
        >
          <Stack gap="xs">
            {parseResult.valid.length < 3 && (
              <Text size="sm" c="red">
                At least 3 valid points are required to form a polygon
              </Text>
            )}

            {parseResult.warnings.length > 0 && (
              <div>
                {parseResult.warnings.map((warning, idx) => (
                  <Text key={idx} size="sm" c="dimmed">
                    • {warning}
                  </Text>
                ))}
              </div>
            )}

            {parseResult.invalid.length > 0 && showErrors && (
              <div>
                <Text size="sm" fw={500} mb="xs">
                  Errors:
                </Text>
                <Stack gap={4}>
                  {parseResult.invalid.slice(0, 5).map((err, idx) => (
                    <Text key={idx} size="xs" c="red" style={{ fontFamily: "monospace" }}>
                      Line {err.line}: {err.error} - {err.original}
                    </Text>
                  ))}
                  {parseResult.invalid.length > 5 && (
                    <Text size="xs" c="dimmed">
                      ... and {parseResult.invalid.length - 5} more error(s)
                    </Text>
                  )}
                </Stack>
              </div>
            )}
          </Stack>
        </Alert>
      )}

      <Group justify="space-between">
        <Group gap="xs">
          <Button size="sm" variant="light" onClick={handleParse} disabled={!input.trim()}>
            Parse
          </Button>
          <Button size="sm" variant="subtle" onClick={handleClear} disabled={!input}>
            Clear
          </Button>
        </Group>

        <Group gap="xs">
          {onCancel && (
            <Button size="sm" variant="default" onClick={onCancel}>
              Cancel
            </Button>
          )}

          {parseResult && parseResult.invalid.length > 0 && parseResult.valid.length >= 3 && (
            <Button size="sm" color="orange" onClick={handleImportValid}>
              Import Valid Only
            </Button>
          )}

          {parseResult && parseResult.invalid.length === 0 && parseResult.valid.length >= 3 && (
            <Button size="sm" color="green" onClick={handleImportAll}>
              Import All ({parseResult.valid.length})
            </Button>
          )}
        </Group>
      </Group>
    </Stack>
  )
}
